import {Component, ElementRef, EventEmitter, Input, OnInit, Output, SecurityContext, ViewChild} from '@angular/core';
import Swal from "sweetalert2";
import swal from "sweetalert2";
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import {wholeNumberValidator} from "../../../../components/validators/whole-number-validator";
import {notBlankValidator} from "../../../../components/validators/not-blank-validator";
import {storedMaxLengthValidator} from "../../../../components/validators/stored-max-length-validator";
import {englishOnlyValidator} from "../../../../components/validators/english-only-validator";
import {
  GRANT_APPLICATION_AMOUNT,
  GRANT_APPLICATION_LIMITS
} from "../../../../constants/dataverse-field-limits";
import {baseFile} from "../../../../models/grantApplication/grantApplicationGetinfoResponse";
import {Router} from "@angular/router";
import {NgxSpinnerService} from "ngx-spinner";
import {CommonService} from "../../../../services/common.service";
import {GrantApplicationService} from "../../../../services/grantApplication.service";
import {ApplicationTargetGroupResponse} from "../../../../models/Common/applicationTargetGroupResponse";
import {applicationImpactResponse} from "../../../../models/Common/applicationimpactResponse";
import {cofundingStatusResponse} from "../../../../models/Common/cofundingStatusResponse";
import {CountryResponse} from "../../../../models/Common/countryResponse";
import {CurrencyResponse} from "../../../../models/Common/currencyResponse";
import Selectr from "mobius1-selectr";
import {
  ApplicationInfo,
  ExistingApplicationInfo,
  TargetGroupWithInfo
} from "../../../../models/grantApplication/grantApplicationResponse";
import {ProjectService} from "../../../../services/project.service";
import {GrantApplicationStatus} from "../../../applications/application-detail/application-detail.component";
import {DomSanitizer} from "@angular/platform-browser";
import {UploadFileService} from "../../../../services/upload-file.service";

export interface ApplicationFormValue {
  onGoingProject: string;
  projectId: string;
  budget: string;
  currencyId: string;
  countryId: string;
  location: string;
  dateRange: Date[]
  applicationName: string;
  applicationDescription: string;
  cofundStatusId: string;
  isContinueExistsProject: boolean | null;
  cofundDescription: string;
  impactId: string;
  targetGroupId: string[];
  targetGroupOther: string;
  grantedAmount: number;
}


@Component({
  selector: 'app-project-form-detail',
  templateUrl: './project-form-detail.component.html',
  styleUrls: ['./project-form-detail.component.scss']
})
export class ProjectFormDetailComponent implements OnInit {
  Validators = Validators;

  @Input() granApplicationId!: string;
  files: baseFile[] | undefined;
  @Input() documentDatas!: FormArray;
  @Input() projectId!: string;

  @Output() filesChanged = new EventEmitter<baseFile[]>(); //

  @Input() loadNotDraft!: boolean;
  @Input() graintApplicationStatus!: GrantApplicationStatus | undefined;
  @Input() showMainControl: boolean = true;
  @Input() isReadOnly: boolean = false;


  // Flag to track if we're in draft mode
  isDraftMode: boolean = false;


  @ViewChild('selectTargetGroupElement') selectElement!: ElementRef;
  targetGroups: ApplicationTargetGroupResponse[] = [];


  /**
   * Format rules enforced against the Dataverse column constraints, kept separate from
   * `Validators.required` so the Save Draft path can drop "required" while still refusing values
   * that the platform would reject. See `applyDraftModeValidators()`.
   *
   * Limits come from `src/app/constants/dataverse-field-limits.ts` (read from live CRM metadata).
   */
  protected readonly limits = GRANT_APPLICATION_LIMITS;
  protected readonly amountRange = GRANT_APPLICATION_AMOUNT;

  private readonly formatValidators: { [controlName: string]: ValidatorFn[] } = {
    budget: [
      wholeNumberValidator(),
      Validators.min(GRANT_APPLICATION_AMOUNT.min),
      Validators.max(GRANT_APPLICATION_AMOUNT.max)
    ],
    location: [notBlankValidator(), Validators.maxLength(GRANT_APPLICATION_LIMITS.location)],
    applicationName: [
      notBlankValidator(),
      Validators.maxLength(GRANT_APPLICATION_LIMITS.applicationName),
      englishOnlyValidator()
    ],
    // Textareas use storedMaxLengthValidator: their line breaks are sent as CRLF, so each one costs
    // two characters of the Dataverse budget. The single-line fields above cannot contain newlines.
    applicationDescription: [
      notBlankValidator(),
      storedMaxLengthValidator(GRANT_APPLICATION_LIMITS.applicationDescription)
    ],
    onGoingProject: [notBlankValidator(), storedMaxLengthValidator(GRANT_APPLICATION_LIMITS.onGoingProject)],
    cofundDescription: [notBlankValidator(), storedMaxLengthValidator(GRANT_APPLICATION_LIMITS.cofundDescription)],
    targetGroupOther: [notBlankValidator()],
  };

  applicationForm: FormGroup = new FormGroup({
      projectId: new FormControl(''),
      budget: new FormControl('', [Validators.required, ...this.formatValidators['budget']]),
      currencyId: new FormControl('', [Validators.required]),
      countryId: new FormControl('', [Validators.required]),
      location: new FormControl('', [Validators.required, ...this.formatValidators['location']]),
      startDate: new FormControl('', [Validators.required]),
      endDate: new FormControl('', [Validators.required]),
      applicationName: new FormControl('', [Validators.required, ...this.formatValidators['applicationName']]),
      applicationDescription: new FormControl('', [Validators.required, ...this.formatValidators['applicationDescription']]),
      onGoingProject: new FormControl('', [Validators.required, ...this.formatValidators['onGoingProject']]),
      cofundStatusId: new FormControl('', [Validators.required]),
      isContinueExistsProject: new FormControl(null, [Validators.required]),
      cofundDescription: new FormControl('', [Validators.required, ...this.formatValidators['cofundDescription']]),
      impactId: new FormControl('', [Validators.required]),
      targetGroupId: new FormControl([], [Validators.required]),
      targetGroupOther: new FormControl('', [...this.formatValidators['targetGroupOther']]),
      grantedAmount: new FormControl({value: '', disabled: true})
    }, {validators: this.dateRangeValidator()}
  );
  selectr: any;
  @Input() redirectAfterSubmit: boolean = true;
  @Input() showMiniControl: boolean = false;
  protected impact: applicationImpactResponse[] = [];

  getImpactName(id: string | null | undefined): string {
    if (!id) return '';
    return this.impact.find(i => i.id === id)?.name ?? '';
  }

  selectImpact(id: string): void {
    const ctrl = this.applicationForm.get('impactId');
    ctrl?.setValue(id);
    ctrl?.markAsTouched();
    ctrl?.markAsDirty();
  }

  protected cofundingStatus: cofundingStatusResponse[] = [];
  protected countries: CountryResponse[] = [];
  protected currency: CurrencyResponse[] = [];
  protected isFormLoaded : boolean = false;

  constructor(
    private spinner: NgxSpinnerService,
    private commonService: CommonService,
    private projectService: ProjectService,
    private grantApplicationService: GrantApplicationService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private uploadFile: UploadFileService,
  ) {

  }

  updateFormControl() {

    // const selectedValues = this.selectedItems.map(item => item.value);
    const selectedValues = this.selectr.getValue();
    const selectedIds = selectedValues.map((item: any) => item.match(/'([^']+)'/)[1]);
    console.log('selectedValues', selectedIds);
    this.applicationForm.get('targetGroupId')?.setValue(selectedIds);
    this.applicationForm.get('targetGroupId')?.markAsTouched();
    this.applicationForm.get('targetGroupId')?.updateValueAndValidity();

    console.log(this.applicationForm.get('targetGroupId')?.value);
  }

  initializeSelectr(): void {
    const options = {
      multiple: true,
      searchable: false,
      clearable: true,
      allowDeselect: true,
      taggable: false,
      nativeDropdown: false,
      placeholder: 'Select target groups...',
    }

     this.selectr = new Selectr(this.selectElement.nativeElement, options);


    this.applicationForm.statusChanges.subscribe(_ => {
      this.validateTargetGroup();
    });


    this.selectr.on('selectr.change', (_: any) => {
      this.updateFormControl();
    });


    const initialValue = this.applicationForm.get('targetGroupId')?.value;
    if (initialValue) {
      this.selectr.setValue(initialValue);
      this.updateSelectedItems();
    }
  }

  updateSelectedItems() {
    Array.from(this.selectr.selected)
      .map((option: any) => ({
        value: option.value,
        text: option.textContent
      }));
  }

  dateRangeValidator(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const startDate = formGroup.get('startDate')?.value;
      const endDate = formGroup.get('endDate')?.value;

      if (startDate && endDate && new Date(startDate) >= new Date(endDate)) {
        return {invalidDateRange: true}; // Error object
      }
      return null; // No errors
    };
  }

  validateTargetGroup() {
    const targetGroupControl = this.applicationForm.get('targetGroupId');
    const selectedElement = this.selectElement.nativeElement.parentElement.querySelector('.selectr-selected');

    if (targetGroupControl?.invalid && targetGroupControl?.touched && !this.isDraftMode) {
      console.log('invalid and touched');
      selectedElement.classList.add('is-invalid');
    } else {
      console.log('valid or not touched');
      selectedElement.classList.remove('is-invalid');
    }
  }

  clearUploadedFiles(): void {

    while (this.documentDatas.length !== 0) {
      this.documentDatas.removeAt(0);
    }
    console.log('Uploaded files cleared from the form.');
  }

  async onSubmit() {
    this.applicationForm.markAllAsTouched();
    this.applicationForm.updateValueAndValidity();

    if (this.applicationForm.invalid) {
      await this.handleFormInvalid();
      return;
    }

    const uploadFileFromAnotherComponent = this.uploadFile.getFile()?.length;
    const existingFilesValid = (this.files && this.files.length > 0 || uploadFileFromAnotherComponent > 0);




    if (!existingFilesValid && this.documentDatas.length === 0) {
      await Swal.fire({
        icon: 'warning',
        title: 'File Attachment Required',
        text: 'Please attach at least one file before submitting the application.',
      });
      return;
    }


    const formData = this.prepareFormData();

    await this.spinner.show();

    // case save draft => submit
    if (this.granApplicationId) {

      formData.append('GrantApplicationId', this.granApplicationId || '');
    }


    this.grantApplicationService.create(formData).subscribe({
      next: async (result) => {

        this.spinner.hide();
        if (result.result) {

          if (!result.hasWarning) {
            console.log('Document uploaded successfully');
            this.clearUploadedFiles();


            await swal.fire({
              title: "The application has been successfully submitted.",
              icon: 'success',
              confirmButtonText: 'OK',
            }).then(() => {
              if (this.redirectAfterSubmit) {
                this.router.navigate(['/funding-opportunities']);
              }
            });
          } else {
            await swal.fire({
              title: 'Warning',
              text: result.messages?.join('\n') || '',
              icon: 'warning',
              confirmButtonText: 'OK',
            });
          }


        }
        // A result:false body is reported once by HttpErrorInterceptor, which has the
        // field-level detail and the support reference id. Alerting here too would double it.

        this.spinner.hide();
      },
      error: async () => {
        await this.spinner.hide();
      },
    });
  }

  async ngOnInit(): Promise<void> {

    console.log('this.isReadOnly', this.isReadOnly);
    await this.spinner.show();
    Promise.all([
      this.loadCurrency(),
      this.loadCountry(),
      this.loadTargetGroups(),
      this.loadApplicationImpact(),
      this.loadCofundingStatus()
    ]).then(() => {

      if (this.graintApplicationStatus !== "Approved" && !this.isReadOnly) {
        setTimeout(() => {
          this.initializeSelectr();
        }, 100);
      }


    }).finally(() =>{

      this.spinner.hide();
    } );

    this.setupCofundingValidation();
    this.setupTargetGroupOtherValidation();


    if (this.loadNotDraft) {
      const result = await this.loadApplication();

      if (result) {

        if (this.graintApplicationStatus === "Approved" || this.isReadOnly) {
          this.loadTargetGroupsFromTargetGroupWithInfo(result.targetGroupWithInfos);
        }

        const onlyInactive = result.targetGroupWithInfos.filter(x => !x.isActive).map(x => x.id);

        const allValue = this.applicationForm.get('targetGroupId')?.value as string[];


        const filteredValue = allValue.filter(value => !onlyInactive.includes(value));


        this.applicationForm.get('targetGroupId')?.setValue(filteredValue);

      }


    } else {

      await this.loadExistingApplication();

    }

    this.applicationForm.get('projectId')?.setValue(this.projectId);

    // The start/end date relationship is enforced by the form-level dateRangeValidator() registered
    // on the FormGroup, so no manual valueChanges handling is needed here.

    if (this.graintApplicationStatus === "Approved" || this.isReadOnly) {
      this.applicationForm.disable();


      const removeButtons = document.querySelectorAll('.selectr-tag-remove');
      removeButtons.forEach((button: Element) => {
        (button as HTMLButtonElement).disabled = true;
        button.setAttribute('style', 'pointer-events: none; opacity: 0.6;');
      })

      const clearButtons = document.querySelectorAll('.selectr-clear');

      clearButtons.forEach((button: Element) => {
        (button as HTMLButtonElement).disabled = true;
        button.setAttribute('style', 'pointer-events: none; opacity: 0.6;');
      });


    }
  }

  async onSaveDraft() {

    const originalValidators = this.storeOriginalValidators();

    // Set draft mode flag
    this.isDraftMode = true;

    this.applyDraftModeValidators();

    // Only mark applicationName as touched since it's the only required field in draft mode
    this.applicationForm.get('applicationName')?.markAsTouched();
    this.applicationForm.updateValueAndValidity();

    const applicationData = this.prepareFormData();

    if (this.applicationForm.invalid) {
      await this.handleFormInvalid(true); // Pass true to indicate draft mode

      this.restoreValidators(originalValidators);
      return;
    }



    await this.spinner.show();

    this.grantApplicationService.SaveDraft(applicationData).subscribe({
      next: async (result) => {
        try {
          this.spinner.hide();
          if (result.result) {
            if (!result.hasWarning) {
              console.log('Document uploaded successfully');
              this.clearUploadedFiles();
              await swal.fire({
                title: "The draft has been successfully saved.",
                icon: 'success',
                confirmButtonText: 'OK',
              }).then(() => {
                this.router.navigate(['/funding-opportunities']);
              });
            } else {
              await swal.fire({
                title: 'Warning',
                text: result.messages?.join('\n') || '',
                icon: 'warning',
                confirmButtonText: 'OK',
              });
            }
          }
          // A result:false body is reported once by HttpErrorInterceptor, which has the
          // field-level detail and the support reference id. Alerting here too would double it.
        } finally {

          this.restoreValidators(originalValidators);
          await this.spinner.hide();
        }
      },
      error: async () => {
        try {
          await this.spinner.hide();
        } finally {

          this.restoreValidators(originalValidators);
        }
      },
    });
  }

  updateTargetGroup(targetGroupId: string[]) {

    let testValue: string[] = [];

    const select = this.selectElement.nativeElement as HTMLSelectElement;
    Array.from(select.options).forEach((opt: HTMLOptionElement) => {
      testValue.push(opt.value);
    });

    let filterArray: string[] = []
    targetGroupId.forEach(exisingValue => {
      const filterValue = testValue.filter(item => item.includes(exisingValue));

      filterArray = [...filterArray, ...filterValue];
    })


    if (this.selectr) {
      this.selectr.setValue(filterArray);
    }

  }

  private sanitize(content: string): string {
    return this.sanitizer.sanitize(SecurityContext.HTML, content) || '';
  }




  loadCurrency() {
    return new Promise<void>((resolve, reject) => {
      this.commonService.getCurrency().subscribe({
        next: (response) => {
          this.currency = response.item || [];
          resolve();
        },
        error: (error) => {
          console.error("Error loading currency", error);
          reject(error);
        },
      });
    });
  }

  loadTargetGroups() {
    return new Promise<void>((resolve, reject) => {
      this.commonService.getApplicationTarget().subscribe({
        next: (response) => {
          const result = (response.item || []);


          this.targetGroups = result.map((group: any) => ({
            ...group,
            name: this.sanitize(group.name)
          }));

          resolve();
        },
        error: (error) => {
          console.error("Error loading impact", error);
          reject(error);
        },
      });
    });
  }

  /**
   * Fix the case inactive target group is selected but does not show in target group.
   *
   * Transforms and loads target group data into the targetGroups property.
   *
   * @param {TargetGroupWithInfo[]} targetGroupWithInfo - An array of target group objects containing detailed information.
   * @return {void} This method does not return anything. It updates the targetGroups property by transforming and appending formatted data.
   */
  loadTargetGroupsFromTargetGroupWithInfo(targetGroupWithInfo: TargetGroupWithInfo[]) {

    this.targetGroups = targetGroupWithInfo.map(item => {
      return {
        id: item.id,
        name: this.sanitize(item.targetGroupName)
      } as ApplicationTargetGroupResponse;
    });
  }

  loadCountry() {
    return new Promise<void>((resolve, reject) => {
      this.commonService.getCountry().subscribe({
        next: (response) => {
          this.countries = response.item || [];
          resolve();
        },
        error: (error) => {
          console.error("Error loading currency", error);
          reject(error);
        },
      });
    });
  }

  loadCofundingStatus() {
    return new Promise<void>((resolve, reject) => {
      this.commonService.getCofundingStatus().subscribe({
        next: (response) => {
          this.cofundingStatus = response.item || [];
          resolve();
        },
        error: (error) => {
          console.error("Error loading getCofundingStatus", error);
          reject(error);
        },
      });
    });
  }

  loadApplicationImpact() {
    return new Promise<void>((resolve, reject) => {
      this.commonService.getApplicationImpact().subscribe({
        next: (response) => {
          this.impact = response.item || [];
          resolve();
        },
        error: (error) => {
          console.error("Error loading targetGroup", error);
          reject(error);
        },
      });
    });
  }

  private prepareFormData(): FormData {
    const formValue: ApplicationFormValue = this.applicationForm.value as ApplicationFormValue;

    const formData = new FormData();


    formData.append('GrantApplicationId', this.granApplicationId || '');
    formData.append('ProjectId', formValue.projectId || '');
    formData.append('Budget', formValue.budget || '');
    formData.append('CurrencyId', formValue.currencyId || '');
    formData.append('CountryId', formValue.countryId || '');
    formData.append('Location', formValue.location || '');
    formData.append('onGoingProject', formValue.onGoingProject || '');

    const startDate = this.applicationForm.get('startDate')?.value;
    const endDate = this.applicationForm.get('endDate')?.value;


    if (startDate) {
      const validStartDate = `${startDate.getFullYear()}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}-${startDate.getDate().toString().padStart(2, '0')}`;
      formData.append('StartDate', validStartDate);
    }
    if (endDate) {
      const validEndDate = `${endDate.getFullYear()}-${(endDate.getMonth() + 1).toString().padStart(2, '0')}-${endDate.getDate().toString().padStart(2, '0')}`;
      formData.append('EndDate', validEndDate);
    }

    formData.append('ApplicationName', formValue.applicationName || '');
    formData.append('ApplicationDescription', formValue.applicationDescription || '');
    formData.append('IsContinueExistsProject', `${formValue.isContinueExistsProject}`);
    formData.append('CofundStatusId', formValue.cofundStatusId || '');
    formData.append('CofundDescription', formValue.cofundDescription || '');
    formData.append('ImpactId', formValue.impactId || '');
    formData.append('typeOfSupportId', '1');


    // Add target group information
    formValue.targetGroupId
      .filter(id => id !== 'other')
      .forEach((id: string) => {
        formData.append('TargetGroupId', id);
      });
    formData.append('TargetGroupOther', formValue.targetGroupOther || '');


    this.documentDatas.controls.forEach((control: any) => {
      if (control.value.file) {
        this.appendFileToFormData(formData, control.value.file);
      }
    });

    return formData;
  }

  private appendFileToFormData(formData: FormData, file: File): void {
    formData.append('Files', file, file.name);
  }

  private loadApplicationInfo(existingApplicationInfo: ExistingApplicationInfo): void {

    const applicationInfo = existingApplicationInfo.applicationInfo;

    if (!applicationInfo) return;

    const startDateObj = new Date(applicationInfo.startDate);
    const endDateObj = new Date(applicationInfo.endDate);


    this.granApplicationId = applicationInfo.grantApplicationId;

    this.files = existingApplicationInfo.reportFiles;

    this.filesChanged.emit(this.files);

    this.applicationForm.patchValue({
      projectId: applicationInfo.projectId,
      budget: applicationInfo.budget,
      currencyId: applicationInfo.currencyId,
      countryId: applicationInfo.countryId,
      location: applicationInfo.location,
      dateRange: [startDateObj, endDateObj],
      startDate: startDateObj,
      endDate: endDateObj,
      applicationName: applicationInfo.applicationName,
      applicationDescription: applicationInfo.applicationDescription,
      onGoingProject: applicationInfo.onGoingProject,
      cofundStatusId: applicationInfo.cofundStatusId,
      isContinueExistsProject: applicationInfo.isContinueExistsProject,
      cofundDescription: applicationInfo.cofundDescription,
      impactId: applicationInfo.impactId,
      targetGroupId: applicationInfo.targetGroupId || [],
      targetGroupOther: applicationInfo.targetGroupOther,
      grantedAmount: applicationInfo.grantedAmount
    });

    this.updateTargetGroup(applicationInfo.targetGroupId);


    console.log("Standard application info loaded:", applicationInfo);
  }

  private async loadExistingApplication(): Promise<ApplicationInfo | null> {
    try {
      const projectId = this.projectId;

      if (!projectId) {
        console.warn("No project ID available to fetch existing application.");
        return null;
      }


      await this.spinner.show();


      const payload = {
        projectId: projectId,
        isEmergencyApplication: false
      };


      const response = await this.projectService.ExistingApplication(payload).toPromise();


      if (response?.result && response?.item) {
        const existingApplication = response.item;

        if (existingApplication.applicationInfo) {
          this.loadApplicationInfo(existingApplication);
          return existingApplication.applicationInfo;
        }

        console.log("Existing application loaded successfully.");
      } else {
        console.warn("No existing application found or request failed.");
        console.warn(response?.messages);
      }
    } catch (error) {
      console.error("Error occurred while loading existing application:", error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load existing application data. Please try again later.',
      });
    } finally {
      await this.spinner.hide();
    }
    return null;
  }

  private async loadApplication(): Promise<ApplicationInfo | null> {
    try {
      const projectId = this.projectId;

      if (!projectId) {
        console.warn("No project ID available to fetch existing application.");
        return null;
      }


      await this.spinner.show();


      const payload = {
        projectId: projectId,
        isEmergencyApplication: false,
        applicationId: this.granApplicationId
      };


      const response = await this.projectService.ExistingNoDraftApplication(payload).toPromise();


      if (response?.result && response?.item) {
        const existingApplication = response.item;


        if (existingApplication.applicationInfo) {
          this.loadApplicationInfo(existingApplication);
          return existingApplication.applicationInfo;
        }

        console.log("Existing application loaded successfully.");
      } else {
        console.warn("No existing application found or request failed.");
        console.warn(response?.messages);

      }
    } catch (error) {
      console.error("Error occurred while loading existing application:", error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load existing application data. Please try again later.',
      });
    } finally {
      await this.spinner.hide();
    }
    return null;
  }

  private async handleFormInvalid(isDraftMode: boolean = false) {
    if (isDraftMode) {
      // In draft mode, only mark applicationName as touched
      this.applicationForm.get('applicationName')?.markAsTouched({onlySelf: true});
    } else {
      // In normal mode, mark all fields as touched
      Object.keys(this.applicationForm.controls).forEach(field => {
        const control = this.applicationForm.get(field);
        control?.markAsTouched({onlySelf: true});
      });
    }

    this.validateTargetGroup();

    await swal.fire({
      title: 'Form Invalid',
      html: 'Please correct the invalid field.',
      icon: 'warning',
      confirmButtonText: 'OK',
    });
  }

  private setupCofundingValidation(): void {
    const cofundStatusIdControl = this.applicationForm.get('cofundStatusId');
    const cofundDescriptionControl = this.applicationForm.get('cofundDescription');

    cofundStatusIdControl?.valueChanges.subscribe((value: string) => {
      // Keep the length limit in both branches - only "required" is conditional.
      const validators = this.formatValidatorsFor('cofundDescription');
      cofundDescriptionControl?.setValidators(
        value === "1" ? [Validators.required, ...validators] : validators
      );
      cofundDescriptionControl?.updateValueAndValidity();
    });
  }

  /**
   * "Other" target group needs a description only while "Other" is selected, but the text must
   * never be whitespace-only when it is present.
   */
  private setupTargetGroupOtherValidation(): void {
    const targetGroupIdControl = this.applicationForm.get('targetGroupId');
    const targetGroupOtherControl = this.applicationForm.get('targetGroupOther');

    targetGroupIdControl?.valueChanges.subscribe((value: string[]) => {
      const validators = this.formatValidatorsFor('targetGroupOther');
      const isOtherSelected = Array.isArray(value) && value.includes('other');
      targetGroupOtherControl?.setValidators(
        isOtherSelected ? [Validators.required, ...validators] : validators
      );
      targetGroupOtherControl?.updateValueAndValidity({emitEvent: false});
    });
  }

  /**
   * The non-"required" validators for a control - the rules that must hold on the Save Draft path
   * too, because Dataverse rejects the value regardless of whether the application is complete.
   */
  private formatValidatorsFor(controlName: string): ValidatorFn[] {
    return this.formatValidators[controlName] ?? [];
  }

  /**
   * Stores the original validators for all form controls
   * @returns A map of control names to their validators
   */
  private storeOriginalValidators(): Map<string, ValidatorFn | ValidatorFn[] | null> {
    const validators = new Map<string, ValidatorFn | ValidatorFn[] | null>();

    Object.keys(this.applicationForm.controls).forEach(controlName => {
      const control = this.applicationForm.get(controlName);
      if (control) {
        validators.set(controlName, control.validator);
      }
    });

    return validators;
  }

  /**
   * Applies draft mode validators - only applicationName is required.
   *
   * Format rules (length, whole number, range, non-blank) are deliberately kept: a draft is still
   * written to Dataverse, so a value the platform would reject must be blocked here as well.
   * Sets default value for isContinueExistsProject to false
   */
  private applyDraftModeValidators(): void {
    // Drop "required" for all controls except applicationName, but keep their format rules.
    Object.keys(this.applicationForm.controls).forEach(controlName => {
      const control = this.applicationForm.get(controlName);
      if (control && controlName !== 'applicationName') {
        control.setValidators(this.formatValidatorsFor(controlName));
        control.updateValueAndValidity();
      }
    });

    // Set default value for isContinueExistsProject to false
    const isContinueExistsProjectControl = this.applicationForm.get('isContinueExistsProject');
    if (isContinueExistsProjectControl && isContinueExistsProjectControl.value === null) {
      isContinueExistsProjectControl.setValue(false);
    }
  }

  /**
   * Restores the original validators for all form controls
   * @param originalValidators Map of control names to their original validators
   */
  private restoreValidators(originalValidators: Map<string, ValidatorFn | ValidatorFn[] | null>): void {
    // Reset draft mode flag
    this.isDraftMode = false;

    originalValidators.forEach((validator, controlName) => {
      const control = this.applicationForm.get(controlName);
      if (control) {
        control.setValidators(validator);
        control.updateValueAndValidity();
      }
    });
  }

}
